import type { GA4 } from "react-ga4/types/ga4"

// Marketing/attribution params safe to forward to GA4. Everything else (auth
// tokens, email, unknown PII) is stripped from URLs before they leave the app.
// Prefix rules also allow `utm_*` and `sfmc_*` (Salesforce email) keys.
const ALLOWED_QUERY_PARAMS = new Set([
  // Google / Microsoft paid + cross-domain
  "gclid",
  "gbraid",
  "wbraid",
  "gclsrc",
  "dclid",
  "gad_source",
  "gad_campaignid",
  "msclkid",
  "_gl",
  // Google auto-tagging cluster (non-PII, only appear alongside gclid)
  "matchtype",
  "network",
  "adposition",
  "device",
  // Social ad-click
  "fbclid",
  "ttclid",
  "twclid",
  "li_fat_id",
])

/**
 * Remove PII from a URL
 *
 * If the URL can't be parsed it will be returned un-changed. It won't throw an
 * error so it should be safe to use for cleaning GA data
 */
export const cleanPiiUrl = (value: string): string => {
  try {
    const decodedString = decodeURIComponent(value)
    const url = new URL(decodedString)
    // Snapshot keys first: deleting while iterating searchParams skips entries.
    for (const key of Array.from(url.searchParams.keys())) {
      const isAllowed =
        ALLOWED_QUERY_PARAMS.has(key) || key.startsWith("utm_") || key.startsWith("sfmc_")
      if (!isAllowed) url.searchParams.delete(key)
    }

    return url.href
  } catch (_) {
    return value // Don't stop GA from sending data, just carry on as normal
  }
}

/**
 * Clean all PII from GA data before we send it
 *
 * We do this by "monkey patching" the `_gtag` function the react-ga4 client.
 * The patch will intercept the GA payload, update the payload to omit any PII,
 * then send off the "cleaned" payload as normal
 *
 * Ideally we wouldn't patch, but this way we can ensure _all_ events are
 * cleaned of PII
 */
export const interceptPii = <T extends GA4>(reactGA4: T): T => {
  const originalGtag = reactGA4._gtag.bind(reactGA4)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isPlainObject = (value: any): value is Record<string, any> =>
    typeof value === "object" && value !== null && !Array.isArray(value)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cleanPayload = (params: Record<string, any>) => {
    // Shallow copy so we don't mutate the original object by reference
    const cleaned = { ...params }
    if ("page_location" in cleaned) cleaned.page_location = cleanPiiUrl(cleaned.page_location)
    if ("page_referrer" in cleaned) cleaned.page_referrer = cleanPiiUrl(cleaned.page_referrer)
    if ("full_page_url" in cleaned) cleaned.full_page_url = cleanPiiUrl(cleaned.full_page_url)
    if ("dimension12" in cleaned) cleaned.dimension12 = cleanPiiUrl(cleaned.dimension12)
    return cleaned
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reactGA4._gtag = (command: string, action: any, ...args: any[]) => {
    // react-ga4 passes the PII-bearing payload as the 2nd arg for `set`
    // (gtag("set", fields)) but as the 3rd for event/config. Clean whichever holds it.
    if (command === "set" && isPlainObject(action)) {
      action = cleanPayload(action)
    } else if (isPlainObject(args[0])) {
      args[0] = cleanPayload(args[0])
    }

    originalGtag(command, action, ...args)
  }

  // For `page_location` we need access to `window`. In NextJS we don't have
  // access to `window` at build time
  if (typeof window !== "undefined") {
    const cleanLocation = cleanPiiUrl(window.location.href)
    const cleanReferrer = cleanPiiUrl(document.referrer)
    reactGA4.set({
      page_location: cleanLocation,
      page_referrer: cleanReferrer,
    })
  }

  return reactGA4
}
